import { useEffect, type ReactElement } from 'react';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { LineChart } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SurfaceBreadcrumbRow from '@/shared/surfaces/SurfaceBreadcrumbRow';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import { resolveDateRange } from '@/features/global-filters/GlobalFilterTypes';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import { AdoptionViewModel } from './AdoptionViewModel';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import AdoptionScopeTrail from './AdoptionScopeTrail';
import AdoptionDriverCharts from './AdoptionDriverCharts';
import AdoptionDriverGrid from './AdoptionDriverGrid';

/**
 * Adoption drivers & trends: a time-series per driver plus a groupable grid.
 * Group by any mix of directory, prompt and cost dimensions via the pills, then
 * click a row to drill the trends above to that group or person.
 */
export default function AdoptionDriversPage(): ReactElement {
    const surface = useSurface('behavior');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <AdoptionDriversBody surface={surface} />
        </SurfaceAssistScope>
    );
}

function AdoptionDriversBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const vm = useViewModel(AdoptionViewModel);
    const ambientFilters = useAssistFilters();
    const { snapshot } = useGlobalFilters();

    useEffect(() => {
        vm.setAmbientFilters([...ambientFilters]);
        void vm.loadDetail();
    }, [vm, ambientFilters]);

    // Weekly trend charts need more than a week of range to be meaningful.
    const bounds = resolveDateRange(snapshot.dateRange);
    const rangeDays = Math.round((bounds.end.getTime() - bounds.start.getTime()) / (24 * 60 * 60 * 1000));
    const showTrend = rangeDays > 7;

    const ready = !vm.loading && vm.score !== null && vm.trend !== null;
    const scopeLabel = vm.scopePath.length === 0 ? null : (vm.scopePath[vm.scopePath.length - 1]?.label ?? vm.scopePath[vm.scopePath.length - 1]?.value ?? null);

    return (
        <>
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <SurfaceBreadcrumbRow items={[{ label: 'Adoption', to: '/adoption' }, { label: 'Drivers & Trends' }]} />

            <PageHeaderComponent
                icon={LineChart}
                eyebrow="Adoption · Scorecards"
                title="Adoption — Drivers & Trends"
                subtitle="Time-series for each driver, with a groupable grid. Group by any mix of directory, prompt and cost dimensions, then click a row to filter the trends above."
            />

            {vm.loading ? (
                <Center py="xl">
                    <Loader />
                </Center>
            ) : !ready || vm.score === null || vm.trend === null ? (
                <Center py="xl">
                    <Text c="dimmed">No adoption data available for the current filters.</Text>
                </Center>
            ) : (
                <Stack gap="md">
                    <AdoptionScopeTrail path={vm.scopePath} onNavigate={(path) => void vm.drillTo(path)} />

                    <AdoptionDriverCharts score={vm.score} trend={vm.trend} showTrend={showTrend} />

                    <SurfaceCard $padding="md" $borderless>
                        <Text fw={700} style={{ fontSize: 15 }} mb={4}>
                            Adoption by dimension
                        </Text>
                        <Text size="xs" c="dimmed" mb="md">
                            {scopeLabel === null
                                ? 'Trends above show the entire organization. Group with the pills, expand rows, and click any row to filter the trends.'
                                : `Trends above are filtered to ${scopeLabel}. Click another row to compare, or a breadcrumb pill to reset.`}
                        </Text>
                        <AdoptionDriverGrid rows={vm.breakdown} onDrill={(path) => void vm.drillTo(path)} />
                    </SurfaceCard>
                </Stack>
            )}
        </>
    );
}
